import React, { useCallback, useMemo } from 'react';
import { Button, Checkbox, DateTimeRangePicker, NumericInput, PropertyGrid, Selector, TabPane } from '@leverege/ui-elements';
import I18N from '@leverege/i18n';
import { useValueChange } from '@leverege/ui-hooks';
import Model from "./HeatmapModel.js";
import ColorRangeEditor from "./views/ColorRangeEditor.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t,
  tIcon
} = I18N.ns('molten.dataViewers.googleMap.HeatmapEditor');
export default function HeatmapEditor(props) {
  const {
    value,
    eventData
  } = props;
  const onModelChange = useValueChange(Model, props);
  const onHelp = useCallback(() => {
    window.open('https://deck.gl/docs/api-reference/aggregation-layers/heatmap-layer#properties', '_blank');
  }, []);
  const aggregationValues = useMemo(() => {
    return [{
      name: t('sum'),
      value: 'SUM'
    }, {
      name: t('mean'),
      value: 'MEAN'
    }];
  }, []);
  const range = value ? Model.getRange(value) : null;
  const timeValue = useMemo(() => {
    if (typeof range === 'string') {
      return range;
    }
    const ret = {
      ...range
    };
    if (ret.start != null && !(ret.start instanceof Date)) {
      ret.start = new Date(ret.start);
    }
    if (ret.end != null && !(ret.end instanceof Date)) {
      ret.end = new Date(ret.end);
    }
    return ret;
  }, [range]);
  return /*#__PURE__*/_jsxs(TabPane, {
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('visibility'),
      tabId: "visibility",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        variant: "heatmapEditor|editor",
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Checkbox, {
            eventData: "setVisible",
            value: Model.getVisible(value),
            onChange: onModelChange,
            label: t('visible')
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('showZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setShowAtZoom",
            value: Model.getShowAtZoom(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('hideZoom'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setHideAtZoom",
            value: Model.getHideAtZoom(value),
            onChange: onModelChange
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('appearance'),
      tabId: "appearance",
      children: /*#__PURE__*/_jsxs(PropertyGrid, {
        variant: "heatmapEditor|editor",
        children: [/*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('blobRadius'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setBlobRadius",
            value: Model.getBlobRadius(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('colorRange'),
          children: /*#__PURE__*/_jsx(ColorRangeEditor, {
            value: value
            // eslint-disable-next-line react/destructuring-assignment
            ,
            onChange: props.onChange,
            eventData: eventData,
            model: Model.colorRanges()
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('intensity'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            float: true,
            eventData: "setIntensity",
            value: Model.getIntensity(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('threshold'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            float: true,
            min: 0,
            max: 1,
            eventData: "setThreshold",
            value: Model.getThreshold(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('aggregation'),
          children: /*#__PURE__*/_jsx(Selector, {
            eventData: "setAggregation",
            value: Model.getAggregation(value),
            onChange: onModelChange,
            values: aggregationValues
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('weightsTextureSize'),
          children: /*#__PURE__*/_jsx(NumericInput, {
            eventData: "setWeightsTextureSize",
            value: Model.getWeightsTextureSize(value),
            onChange: onModelChange
          })
        }), /*#__PURE__*/_jsx(PropertyGrid.Item, {
          fillCell: true,
          label: /*#__PURE__*/_jsx(Button, {
            variant: "tertiary",
            icon: tIcon('help', 'fa fa-question-circle fa-fw'),
            onClick: onHelp,
            children: "Help"
          })
        })]
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabId: "query",
      tabName: t('query'),
      children: /*#__PURE__*/_jsx(PropertyGrid, {
        variant: "heatmapEditor|editor",
        children: /*#__PURE__*/_jsx(PropertyGrid.Item, {
          label: t('range'),
          children: /*#__PURE__*/_jsx(DateTimeRangePicker, {
            eventData: "setRange",
            value: timeValue,
            onChange: onModelChange
          })
        })
      })
    })]
  });
}